/**
 * Chevrotain Parser for Natural Math Exercise DSL
 *
 * Supports natural writing like:
 * QUESTION_TEXT["Solve"]
 * 25 + W_S_I[name="a", length=2, answer="5"] = 30
 * NUMERIC_KEYPAD[includeOperators=true]
 * WIDGET_ANSWER[type="simple"]
 */
import { CstParser } from 'chevrotain';
import {
  allTokens,
  QUESTION_TEXT,
  TEXT_HIGHLIGHTER,
  COLOR_TEXT,
  BOLD_TEXT,
  HIGHLIGHT_TEXT,
  W_S_I,
  STRING_INPUT,
  W_N_I,
  NUMERIC_INPUT,
  DRAGABLE_BUTTONS_OPTIONS,
  INLINE_DRAG_OPTION,
  RADIO_BUTTONS_OPTIONS,
  NUMERIC_KEYPAD,
  WIDGET_ANSWER,
  TABLE,
  ROW,
  CELL,
  NUMBER_LINE,
  POSITION,
  ARROW,
  COUNTER,
  CLOCK,
  HIGHLIGHT,
  CURSOR,
  VISUAL_FRACTION,
  FRACTION,
  MATCHER,
  PAIR_MATCHER,
  TOP_ROW,
  BOTTOM_ROW,
  ITEM,
  ANSWERS,
  IMAGE,
  VERTICAL_OPERATION,
  SEP,
  CARRY,
  INPUT,
  Underscore,
  RULER,
  PROTRACTOR,
  SET_SQUARE,
  NumberLiteral,
  StringLiteral,
  Identifier,
  LBracket,
  RBracket,
  LBrace,
  RBrace,
  LParen,
  RParen,
  Comma,
  Equals,
  Plus,
  Minus,
  Multiply,
  Divide,
  LessThan,
  GreaterThan,
  Question,
  Newline,
  Space,
  FreeText,
} from '../lexer/tokens';

/**
 * Natural DSL Parser
 *
 * Grammar:
 * - exerciseDocument: QUESTION_TEXT[...] line* tools validation
 * - line: inlineElement+ (implicit newline separation)
 * - inlineElement: FreeText | TEXT_HIGHLIGHTER[...] | W_S_I[...]
 * - tools: (DRAGABLE_BUTTONS_OPTIONS[...] | NUMERIC_KEYPAD[...])*
 * - validation: WIDGET_ANSWER[type="simple"|"expression"]
 */
export class MathExerciseParser extends CstParser {
  constructor() {
    super(allTokens, {
      recoveryEnabled: true,
      nodeLocationTracking: 'full',
    });

    this.performSelfAnalysis();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Top-level Document
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * exerciseDocument: [newlines] question lines tools validation [newlines] [spaces]
   */
  public exerciseDocument = this.RULE('exerciseDocument', () => {
    // Optional leading newlines and spaces
    this.MANY(() => {
      this.OR([{ ALT: () => this.CONSUME(Newline) }, { ALT: () => this.CONSUME(Space) }]);
    });

    // Question (optional)
    this.OPTION(() => {
      this.SUBRULE(this.question);
    });

    // Content blocks: lines and tables (zero or more)
    // GATE: BACKTRACK ensures the loop stops when remaining tokens can't form a
    // valid contentBlock (e.g. trailing indentation spaces before EOF).
    // Note: this causes each block to be parsed speculatively before being committed —
    // acceptable for the short DSLs this engine handles.
    this.MANY2({
      GATE: this.BACKTRACK(this.contentBlock),
      DEF: () => this.SUBRULE(this.contentBlock),
    });

    // Tools (zero or more)
    this.MANY3(() => {
      this.SUBRULE(this.tool);
    });

    // Validation (optional - not needed for display-only/hint exercises)
    this.OPTION2(() => {
      this.SUBRULE(this.validation);
    });
    // Optional trailing newlines and spaces (for indented DSL strings)
    this.MANY4(() => {
      this.OR2([{ ALT: () => this.CONSUME2(Newline) }, { ALT: () => this.CONSUME2(Space) }]);
    });
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // Question Block
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * question: QUESTION_TEXT[value]
   */
  private question = this.RULE('question', () => {
    this.CONSUME(QUESTION_TEXT);
    this.CONSUME(LBracket);
    this.CONSUME(StringLiteral, { LABEL: 'value' });
    this.CONSUME(RBracket);

    // Consume newlines and spaces after question
    this.MANY(() => {
      this.OR([{ ALT: () => this.CONSUME(Newline) }, { ALT: () => this.CONSUME(Space) }]);
    });
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // Content Block (line or table)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * contentBlock: table | line
   *
   * Uses BACKTRACK on table to resolve ambiguity — both table and line
   * can start with whitespace (Space/Newline), so LL(k) lookahead alone
   * can't distinguish them. BACKTRACK speculatively tries parsing table
   * first; if it succeeds, that alternative is chosen.
   */
  private contentBlock = this.RULE('contentBlock', () => {
    this.OR([
      {
        GATE: this.BACKTRACK(this.table),
        ALT: () => this.SUBRULE(this.table),
      },
      {
        GATE: this.BACKTRACK(this.radioButtons),
        ALT: () => this.SUBRULE(this.radioButtons),
      },
      {
        GATE: this.BACKTRACK(this.numberLine),
        ALT: () => this.SUBRULE(this.numberLine),
      },
      {
        GATE: this.BACKTRACK(this.pairMatcher),
        ALT: () => this.SUBRULE(this.pairMatcher),
      },
      {
        GATE: this.BACKTRACK(this.matcher),
        ALT: () => this.SUBRULE(this.matcher),
      },
      {
        GATE: this.BACKTRACK(this.verticalOperation),
        ALT: () => this.SUBRULE(this.verticalOperation),
      },
      { ALT: () => this.SUBRULE(this.line) },
    ]);
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // Table Block
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * table: TABLE[parameterList] { row+ }
   */
  private table = this.RULE('table', () => {
    // Skip leading whitespace/newlines
    this.MANY(() => {
      this.OR2([{ ALT: () => this.CONSUME(Space) }, { ALT: () => this.CONSUME(Newline) }]);
    });

    this.CONSUME(TABLE);
    this.CONSUME(LBracket);
    this.SUBRULE(this.parameterList);
    this.CONSUME(RBracket);

    // Skip whitespace before opening brace
    this.MANY2(() => {
      this.OR3([{ ALT: () => this.CONSUME2(Space) }, { ALT: () => this.CONSUME2(Newline) }]);
    });

    this.CONSUME(LBrace);

    // Skip whitespace after opening brace
    this.MANY3(() => {
      this.OR4([{ ALT: () => this.CONSUME3(Space) }, { ALT: () => this.CONSUME3(Newline) }]);
    });

    // One or more rows
    this.AT_LEAST_ONE(() => {
      this.SUBRULE(this.row);
    });

    this.CONSUME(RBrace);

    // Consume trailing whitespace
    this.MANY4(() => {
      this.OR5([{ ALT: () => this.CONSUME4(Newline) }, { ALT: () => this.CONSUME4(Space) }]);
    });
  });

  /**
   * row: ROW { cell+ }
   */
  private row = this.RULE('row', () => {
    // Skip leading whitespace
    this.MANY(() => {
      this.OR([{ ALT: () => this.CONSUME(Space) }, { ALT: () => this.CONSUME(Newline) }]);
    });

    this.CONSUME(ROW);

    // Skip whitespace before brace
    this.MANY2(() => {
      this.OR2([{ ALT: () => this.CONSUME2(Space) }, { ALT: () => this.CONSUME2(Newline) }]);
    });

    this.CONSUME(LBrace);

    // Skip whitespace after opening brace
    this.MANY3(() => {
      this.OR3([{ ALT: () => this.CONSUME3(Space) }, { ALT: () => this.CONSUME3(Newline) }]);
    });

    // One or more cells
    this.AT_LEAST_ONE(() => {
      this.SUBRULE(this.cell);
    });

    this.CONSUME(RBrace);

    // Skip trailing whitespace
    this.MANY4(() => {
      this.OR4([{ ALT: () => this.CONSUME4(Space) }, { ALT: () => this.CONSUME4(Newline) }]);
    });
  });

  /**
   * cell: CELL [parameterList]? { inlineElement* }
   */
  private cell = this.RULE('cell', () => {
    // Skip leading whitespace
    this.MANY(() => {
      this.OR([{ ALT: () => this.CONSUME(Space) }, { ALT: () => this.CONSUME(Newline) }]);
    });

    this.CONSUME(CELL);

    // Optional parameter list [colspan=2]
    this.OPTION(() => {
      this.CONSUME(LBracket);
      this.SUBRULE(this.parameterList);
      this.CONSUME(RBracket);
    });

    // Skip whitespace before brace
    this.MANY2(() => {
      this.OR2([{ ALT: () => this.CONSUME2(Space) }, { ALT: () => this.CONSUME2(Newline) }]);
    });

    this.CONSUME(LBrace);

    // Cell content: zero or more inline elements (with optional whitespace between)
    this.MANY3(() => {
      this.OR3([
        { ALT: () => this.SUBRULE(this.inlineElement) },
        { ALT: () => this.CONSUME3(Newline) },
      ]);
    });

    this.CONSUME(RBrace);

    // Skip trailing whitespace
    this.MANY4(() => {
      this.OR4([{ ALT: () => this.CONSUME4(Space) }, { ALT: () => this.CONSUME4(Newline) }]);
    });
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // Number Line Block
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * numberLine: NUMBER_LINE[parameterList] { position* }
   */
  private numberLine = this.RULE('numberLine', () => {
    // Skip leading whitespace/newlines
    this.MANY(() => {
      this.OR2([{ ALT: () => this.CONSUME(Space) }, { ALT: () => this.CONSUME(Newline) }]);
    });

    this.CONSUME(NUMBER_LINE);
    this.CONSUME(LBracket);
    this.SUBRULE(this.parameterList);
    this.CONSUME(RBracket);

    // Skip whitespace before opening brace
    this.MANY2(() => {
      this.OR3([{ ALT: () => this.CONSUME2(Space) }, { ALT: () => this.CONSUME2(Newline) }]);
    });

    this.CONSUME(LBrace);

    // Skip whitespace after opening brace
    this.MANY3(() => {
      this.OR4([{ ALT: () => this.CONSUME3(Space) }, { ALT: () => this.CONSUME3(Newline) }]);
    });

    // Zero or more children: positions, counters, highlights, arrows.
    // MANY3 (above) consumed whitespace after '{'; each child consumes its own
    // trailing whitespace — so at every iteration LA(1) is unambiguously
    // POSITION, COUNTER, HIGHLIGHT, or ARROW. Same pattern as table → row → cell.
    this.MANY4(() => {
      this.OR6([
        { ALT: () => this.SUBRULE(this.position) },
        { ALT: () => this.SUBRULE(this.counter) },
        { ALT: () => this.SUBRULE(this.highlight) },
        { ALT: () => this.SUBRULE(this.cursor) },
        { ALT: () => this.SUBRULE(this.arrow) },
      ]);
    });

    this.CONSUME(RBrace);

    // Consume trailing whitespace
    this.MANY5(() => {
      this.OR5([{ ALT: () => this.CONSUME4(Newline) }, { ALT: () => this.CONSUME4(Space) }]);
    });
  });

  /**
   * position: POSITION[value=number] { inlineElement+ }
   */
  private position = this.RULE('position', () => {
    // No leading whitespace: parent's MANY3 / each child's trailing MANY
    // guarantee LA(1) = POSITION when this rule is entered.
    this.CONSUME(POSITION);
    this.CONSUME(LBracket);
    this.SUBRULE(this.parameterList);
    this.CONSUME(RBracket);

    // Skip whitespace before brace
    this.MANY2(() => {
      this.OR2([{ ALT: () => this.CONSUME2(Space) }, { ALT: () => this.CONSUME2(Newline) }]);
    });

    this.CONSUME(LBrace);

    // Skip whitespace after opening brace
    this.MANY3(() => {
      this.OR3([{ ALT: () => this.CONSUME3(Space) }, { ALT: () => this.CONSUME3(Newline) }]);
    });

    // Content: inline elements with optional whitespace between
    // (validated non-empty in cst-visitor)
    this.MANY4(() => {
      this.OR4([
        { ALT: () => this.SUBRULE(this.inlineElement) },
        { ALT: () => this.CONSUME4(Newline) },
      ]);
    });

    this.CONSUME(RBrace);

    // Skip trailing whitespace
    this.MANY5(() => {
      this.OR5([{ ALT: () => this.CONSUME5(Space) }, { ALT: () => this.CONSUME5(Newline) }]);
    });
  });

  /**
   * counter: COUNTER[parameterList]
   *
   * Dynamic subdivision control. step = (max_line - min_line) / counter_value.
   */
  private counter = this.RULE('counter', () => {
    // No leading whitespace: parent's MANY3 / each child's trailing MANY
    // guarantee LA(1) = COUNTER when this rule is entered.
    this.CONSUME(COUNTER);
    this.CONSUME(LBracket);
    this.SUBRULE(this.parameterList);
    this.CONSUME(RBracket);

    // Skip trailing whitespace
    this.MANY2(() => {
      this.OR2([{ ALT: () => this.CONSUME2(Space) }, { ALT: () => this.CONSUME2(Newline) }]);
    });
  });

  /**
   * highlight: HIGHLIGHT[parameterList]
   *
   * Colored range on the axis with optional label.
   */
  private highlight = this.RULE('highlight', () => {
    // No leading whitespace: parent's MANY3 / each child's trailing MANY
    // guarantee LA(1) = HIGHLIGHT when this rule is entered.
    this.CONSUME(HIGHLIGHT);
    this.CONSUME(LBracket);
    this.SUBRULE(this.parameterList);
    this.CONSUME(RBracket);

    // Skip trailing whitespace
    this.MANY2(() => {
      this.OR2([{ ALT: () => this.CONSUME2(Space) }, { ALT: () => this.CONSUME2(Newline) }]);
    });
  });

  /**
   * cursor: CURSOR[value=N, answer=N, color="...", fillRange=bool, label="..."]
   *
   * Interactive draggable cursor on the number line.
   * - value:      initial display position (default: 0)
   * - answer:     correct target position (required)
   * - color?:     hex fill color string
   * - fillRange?: fill between the initial value and current cursor position (default: false)
   * - label?:     display label shown above the cursor ball
   */
  private cursor = this.RULE('cursor', () => {
    // No leading whitespace: parent's MANY4 / each child's trailing MANY
    // guarantee LA(1) = CURSOR when this rule is entered.
    this.CONSUME(CURSOR);
    this.CONSUME(LBracket);
    this.SUBRULE(this.parameterList);
    this.CONSUME(RBracket);

    // Trailing whitespace
    this.MANY2(() => {
      this.OR2([{ ALT: () => this.CONSUME2(Space) }, { ALT: () => this.CONSUME2(Newline) }]);
    });
  });

  /**
   * arrow: ARROW[from=number,to=number] { inlineElement* }
   */
  private arrow = this.RULE('arrow', () => {
    // No leading whitespace: parent's MANY3 / each child's trailing MANY
    // guarantee LA(1) = ARROW when this rule is entered.
    this.CONSUME(ARROW);
    this.CONSUME(LBracket);
    this.SUBRULE(this.parameterList);
    this.CONSUME(RBracket);
    // Skip whitespace before brace
    this.MANY2(() => {
      this.OR2([{ ALT: () => this.CONSUME2(Space) }, { ALT: () => this.CONSUME2(Newline) }]);
    });

    this.CONSUME(LBrace);

    // Skip whitespace after opening brace
    this.MANY3(() => {
      this.OR3([{ ALT: () => this.CONSUME3(Space) }, { ALT: () => this.CONSUME3(Newline) }]);
    });

    // Optional content: inline elements with optional whitespace between.
    this.MANY4(() => {
      this.OR4([
        { ALT: () => this.SUBRULE(this.inlineElement) },
        { ALT: () => this.CONSUME4(Newline) },
      ]);
    });

    this.CONSUME(RBrace);

    // Skip trailing whitespace
    this.MANY5(() => {
      this.OR5([{ ALT: () => this.CONSUME5(Space) }, { ALT: () => this.CONSUME5(Newline) }]);
    });
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // Matcher Block
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * matcher: MATCHER[name="m1"] { matcherRow+ matcherAnswers }
   */
  private matcher = this.RULE('matcher', () => {
    // Skip leading whitespace/newlines
    this.MANY(() => {
      this.OR([{ ALT: () => this.CONSUME(Space) }, { ALT: () => this.CONSUME(Newline) }]);
    });

    this.CONSUME(MATCHER);
    this.CONSUME(LBracket);
    this.SUBRULE(this.parameterList);
    this.CONSUME(RBracket);

    // Skip whitespace before opening brace
    this.MANY2(() => {
      this.OR2([{ ALT: () => this.CONSUME2(Space) }, { ALT: () => this.CONSUME2(Newline) }]);
    });

    this.CONSUME(LBrace);

    // Skip whitespace after opening brace
    this.MANY3(() => {
      this.OR3([{ ALT: () => this.CONSUME3(Space) }, { ALT: () => this.CONSUME3(Newline) }]);
    });

    // One or more rows
    this.AT_LEAST_ONE(() => {
      this.SUBRULE(this.matcherRow);
    });

    // ANSWERS block
    this.SUBRULE(this.matcherAnswers);

    this.CONSUME(RBrace);

    // Consume trailing whitespace
    this.MANY4(() => {
      this.OR4([{ ALT: () => this.CONSUME4(Newline) }, { ALT: () => this.CONSUME4(Space) }]);
    });
  });

  /**
   * matcherRow: ROW { matcherItem+ }
   */
  private matcherRow = this.RULE('matcherRow', () => {
    // Skip leading whitespace
    this.MANY(() => {
      this.OR([{ ALT: () => this.CONSUME(Space) }, { ALT: () => this.CONSUME(Newline) }]);
    });

    this.CONSUME(ROW);

    // Skip whitespace before brace
    this.MANY2(() => {
      this.OR2([{ ALT: () => this.CONSUME2(Space) }, { ALT: () => this.CONSUME2(Newline) }]);
    });

    this.CONSUME(LBrace);

    // Skip whitespace after opening brace
    this.MANY3(() => {
      this.OR3([{ ALT: () => this.CONSUME3(Space) }, { ALT: () => this.CONSUME3(Newline) }]);
    });

    // One or more items
    this.AT_LEAST_ONE(() => {
      this.SUBRULE(this.matcherItem);
    });

    this.CONSUME(RBrace);

    // Skip trailing whitespace
    this.MANY4(() => {
      this.OR4([{ ALT: () => this.CONSUME4(Space) }, { ALT: () => this.CONSUME4(Newline) }]);
    });
  });

  /**
   * matcherItem: ITEM[id="A"] { inlineElement* }
   */
  private matcherItem = this.RULE('matcherItem', () => {
    // Skip leading whitespace
    this.MANY(() => {
      this.OR([{ ALT: () => this.CONSUME(Space) }, { ALT: () => this.CONSUME(Newline) }]);
    });

    this.CONSUME(ITEM);
    this.CONSUME(LBracket);
    this.SUBRULE(this.parameterList);
    this.CONSUME(RBracket);

    // Skip whitespace before brace
    this.MANY2(() => {
      this.OR2([{ ALT: () => this.CONSUME2(Space) }, { ALT: () => this.CONSUME2(Newline) }]);
    });

    this.CONSUME(LBrace);

    // Skip whitespace after opening brace
    this.MANY3(() => {
      this.OR3([{ ALT: () => this.CONSUME3(Space) }, { ALT: () => this.CONSUME3(Newline) }]);
    });

    // Display-only inline content: no inputs / widgets allowed inside ITEM.
    // `displayOnly=true` gates out the `inlineInput` alternative.
    this.MANY4(() => {
      this.OR4([
        { ALT: () => this.SUBRULE(this.inlineElement, { ARGS: [true] }) },
        { ALT: () => this.CONSUME4(Newline) },
      ]);
    });

    this.CONSUME(RBrace);

    // Skip trailing whitespace
    this.MANY5(() => {
      this.OR5([{ ALT: () => this.CONSUME5(Space) }, { ALT: () => this.CONSUME5(Newline) }]);
    });
  });

  /**
   * matcherAnswers: ANSWERS { answerSet+ }
   * Sets separated by optional whitespace/newlines — no comma required between sets.
   * answerSet: [ Identifier (, Identifier)* ]
   */
  private matcherAnswers = this.RULE('matcherAnswers', () => {
    // Skip leading whitespace
    this.MANY(() => {
      this.OR([{ ALT: () => this.CONSUME(Space) }, { ALT: () => this.CONSUME(Newline) }]);
    });

    this.CONSUME(ANSWERS);

    // Skip whitespace before brace
    this.MANY2(() => {
      this.OR2([{ ALT: () => this.CONSUME2(Space) }, { ALT: () => this.CONSUME2(Newline) }]);
    });

    this.CONSUME(LBrace);

    // Skip whitespace after opening brace
    this.MANY3(() => {
      this.OR3([{ ALT: () => this.CONSUME3(Space) }, { ALT: () => this.CONSUME3(Newline) }]);
    });

    // One or more answer sets — comma between sets is optional (whitespace suffices)
    this.AT_LEAST_ONE(() => {
      this.SUBRULE(this.matcherAnswerSet);
      // Consume optional whitespace and/or trailing comma after each set
      this.MANY4(() => {
        this.OR4([
          { ALT: () => this.CONSUME4(Space) },
          { ALT: () => this.CONSUME4(Newline) },
          { ALT: () => this.CONSUME(Comma) },
        ]);
      });
    });

    this.CONSUME(RBrace);

    // Skip trailing whitespace
    this.MANY5(() => {
      this.OR5([{ ALT: () => this.CONSUME5(Newline) }, { ALT: () => this.CONSUME5(Space) }]);
    });
  });

  /**
   * matcherAnswerSet: [ Identifier (, Identifier)* ]
   */
  private matcherAnswerSet = this.RULE('matcherAnswerSet', () => {
    this.CONSUME(LBracket);

    // Skip spaces
    this.MANY(() => {
      this.CONSUME(Space);
    });

    // First id
    this.CONSUME(Identifier, { LABEL: 'ids' });

    // Additional ids
    this.MANY2(() => {
      this.MANY3(() => {
        this.CONSUME2(Space);
      });
      this.CONSUME(Comma);
      this.MANY4(() => {
        this.CONSUME3(Space);
      });
      this.CONSUME2(Identifier, { LABEL: 'ids' });
    });

    // Skip spaces
    this.MANY5(() => {
      this.CONSUME4(Space);
    });

    this.CONSUME(RBracket);
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // Pair Matcher Block
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * pairMatcher: PAIR_MATCHER[name="m1"] { pairMatcherTopSection pairMatcherBottomSection pairMatcherAnswers }
   */
  private pairMatcher = this.RULE('pairMatcher', () => {
    // Skip leading whitespace/newlines
    this.MANY(() => {
      this.OR([{ ALT: () => this.CONSUME(Space) }, { ALT: () => this.CONSUME(Newline) }]);
    });

    this.CONSUME(PAIR_MATCHER);
    this.CONSUME(LBracket);
    this.SUBRULE(this.parameterList);
    this.CONSUME(RBracket);

    // Skip whitespace before opening brace
    this.MANY2(() => {
      this.OR2([{ ALT: () => this.CONSUME2(Space) }, { ALT: () => this.CONSUME2(Newline) }]);
    });

    this.CONSUME(LBrace);

    // Skip whitespace after opening brace
    this.MANY3(() => {
      this.OR3([{ ALT: () => this.CONSUME3(Space) }, { ALT: () => this.CONSUME3(Newline) }]);
    });

    // TOP section
    this.SUBRULE(this.pairMatcherTopSection);

    // BOTTOM section
    this.SUBRULE(this.pairMatcherBottomSection);

    // ANSWERS block (reuse matcherAnswers — same syntax)
    this.SUBRULE(this.matcherAnswers);

    this.CONSUME(RBrace);

    // Consume trailing whitespace
    this.MANY4(() => {
      this.OR4([{ ALT: () => this.CONSUME4(Newline) }, { ALT: () => this.CONSUME4(Space) }]);
    });
  });

  /**
   * pairMatcherTopSection: TOP_ROW { matcherItem+ }
   */
  private pairMatcherTopSection = this.RULE('pairMatcherTopSection', () => {
    this.MANY(() => {
      this.OR([{ ALT: () => this.CONSUME(Space) }, { ALT: () => this.CONSUME(Newline) }]);
    });

    this.CONSUME(TOP_ROW);

    this.MANY2(() => {
      this.OR2([{ ALT: () => this.CONSUME2(Space) }, { ALT: () => this.CONSUME2(Newline) }]);
    });

    this.CONSUME(LBrace);

    this.MANY3(() => {
      this.OR3([{ ALT: () => this.CONSUME3(Space) }, { ALT: () => this.CONSUME3(Newline) }]);
    });

    this.AT_LEAST_ONE(() => {
      this.SUBRULE(this.matcherItem);
    });

    this.CONSUME(RBrace);

    this.MANY4(() => {
      this.OR4([{ ALT: () => this.CONSUME4(Space) }, { ALT: () => this.CONSUME4(Newline) }]);
    });
  });

  /**
   * pairMatcherBottomSection: BOTTOM_ROW { matcherItem+ }
   */
  private pairMatcherBottomSection = this.RULE('pairMatcherBottomSection', () => {
    this.MANY(() => {
      this.OR([{ ALT: () => this.CONSUME(Space) }, { ALT: () => this.CONSUME(Newline) }]);
    });

    this.CONSUME(BOTTOM_ROW);

    this.MANY2(() => {
      this.OR2([{ ALT: () => this.CONSUME2(Space) }, { ALT: () => this.CONSUME2(Newline) }]);
    });

    this.CONSUME(LBrace);

    this.MANY3(() => {
      this.OR3([{ ALT: () => this.CONSUME3(Space) }, { ALT: () => this.CONSUME3(Newline) }]);
    });

    this.AT_LEAST_ONE(() => {
      this.SUBRULE(this.matcherItem);
    });

    this.CONSUME(RBrace);

    this.MANY4(() => {
      this.OR4([{ ALT: () => this.CONSUME4(Space) }, { ALT: () => this.CONSUME4(Newline) }]);
    });
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // Vertical Operation Block (column arithmetic)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * verticalOperation: VERTICAL_OPERATION [op="..."]? { (vopRow | vopSep)+ }
   */
  private verticalOperation = this.RULE('verticalOperation', () => {
    this.MANY(() => {
      this.OR([{ ALT: () => this.CONSUME(Space) }, { ALT: () => this.CONSUME(Newline) }]);
    });

    this.CONSUME(VERTICAL_OPERATION);
    this.OPTION(() => {
      this.CONSUME(LBracket);
      this.SUBRULE(this.parameterList);
      this.CONSUME(RBracket);
    });

    this.MANY2(() => {
      this.OR2([{ ALT: () => this.CONSUME2(Space) }, { ALT: () => this.CONSUME2(Newline) }]);
    });

    this.CONSUME(LBrace);

    this.MANY3(() => {
      this.OR3([{ ALT: () => this.CONSUME3(Space) }, { ALT: () => this.CONSUME3(Newline) }]);
    });

    this.AT_LEAST_ONE(() => {
      this.OR4([
        { ALT: () => this.SUBRULE(this.vopRow) },
        { ALT: () => this.SUBRULE(this.vopCarry) },
        { ALT: () => this.SUBRULE(this.vopSep) },
      ]);
    });

    this.CONSUME(RBrace);

    this.MANY4(() => {
      this.OR5([{ ALT: () => this.CONSUME4(Newline) }, { ALT: () => this.CONSUME4(Space) }]);
    });
  });

  /**
   * vopRow: ROW [parameterList]? { vopCell (, vopCell)* }
   */
  private vopRow = this.RULE('vopRow', () => {
    this.CONSUME(ROW);

    // Optional [op="..."] on the row itself
    this.OPTION(() => {
      this.CONSUME(LBracket);
      this.SUBRULE(this.parameterList);
      this.CONSUME(RBracket);
    });

    this.MANY(() => {
      this.OR([{ ALT: () => this.CONSUME(Space) }, { ALT: () => this.CONSUME(Newline) }]);
    });

    this.CONSUME(LBrace);

    this.MANY2(() => {
      this.OR2([{ ALT: () => this.CONSUME2(Space) }, { ALT: () => this.CONSUME2(Newline) }]);
    });

    this.SUBRULE(this.vopCell);

    this.MANY3(() => {
      this.MANY4(() => {
        this.OR3([{ ALT: () => this.CONSUME3(Space) }, { ALT: () => this.CONSUME3(Newline) }]);
      });
      this.CONSUME(Comma);
      this.MANY5(() => {
        this.OR4([{ ALT: () => this.CONSUME4(Space) }, { ALT: () => this.CONSUME4(Newline) }]);
      });
      this.SUBRULE2(this.vopCell);
    });

    this.MANY6(() => {
      this.OR5([{ ALT: () => this.CONSUME5(Space) }, { ALT: () => this.CONSUME5(Newline) }]);
    });

    this.CONSUME(RBrace);

    this.MANY7(() => {
      this.OR6([{ ALT: () => this.CONSUME6(Space) }, { ALT: () => this.CONSUME6(Newline) }]);
    });
  });

  /**
   * vopCell: NumberLiteral | StringLiteral | Underscore | INPUT[parameterList]
   *        | colorText | boldText | textHighlighter
   */
  private vopCell = this.RULE('vopCell', () => {
    this.OR([
      { ALT: () => this.CONSUME(NumberLiteral) },
      { ALT: () => this.CONSUME(StringLiteral) },
      { ALT: () => this.CONSUME(Underscore) },
      {
        ALT: () => {
          this.CONSUME(INPUT);
          this.CONSUME(LBracket);
          this.SUBRULE(this.parameterList);
          this.CONSUME(RBracket);
        },
      },
      { ALT: () => this.SUBRULE(this.colorText) },
      { ALT: () => this.SUBRULE(this.boldText) },
      { ALT: () => this.SUBRULE(this.textHighlighter) },
    ]);
  });

  /**
   * vopCarry: CARRY { vopCell (, vopCell)* }
   */
  private vopCarry = this.RULE('vopCarry', () => {
    this.CONSUME(CARRY);

    this.MANY(() => {
      this.OR([{ ALT: () => this.CONSUME(Space) }, { ALT: () => this.CONSUME(Newline) }]);
    });

    this.CONSUME(LBrace);

    this.MANY2(() => {
      this.OR2([{ ALT: () => this.CONSUME2(Space) }, { ALT: () => this.CONSUME2(Newline) }]);
    });

    this.SUBRULE(this.vopCell);

    this.MANY3(() => {
      this.MANY4(() => {
        this.OR3([{ ALT: () => this.CONSUME3(Space) }, { ALT: () => this.CONSUME3(Newline) }]);
      });
      this.CONSUME(Comma);
      this.MANY5(() => {
        this.OR4([{ ALT: () => this.CONSUME4(Space) }, { ALT: () => this.CONSUME4(Newline) }]);
      });
      this.SUBRULE2(this.vopCell);
    });

    this.MANY6(() => {
      this.OR5([{ ALT: () => this.CONSUME5(Space) }, { ALT: () => this.CONSUME5(Newline) }]);
    });

    this.CONSUME(RBrace);

    this.MANY7(() => {
      this.OR6([{ ALT: () => this.CONSUME6(Newline) }, { ALT: () => this.CONSUME6(Space) }]);
    });
  });

  /**
   * vopSep: SEP
   */
  private vopSep = this.RULE('vopSep', () => {
    this.CONSUME(SEP);
    // Optional empty braces for visual symmetry: SEP {} or SEP
    this.OPTION(() => {
      this.MANY(() => {
        this.OR([{ ALT: () => this.CONSUME(Space) }, { ALT: () => this.CONSUME(Newline) }]);
      });
      this.CONSUME(LBrace);
      this.MANY2(() => {
        this.OR2([{ ALT: () => this.CONSUME2(Space) }, { ALT: () => this.CONSUME2(Newline) }]);
      });
      this.CONSUME(RBrace);
    });

    this.MANY3(() => {
      this.OR3([{ ALT: () => this.CONSUME3(Space) }, { ALT: () => this.CONSUME3(Newline) }]);
    });
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // Line Block (natural writing!)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * line: [spaces] inlineElement+ Newline [spaces]
   *
   * A line is a sequence of inline elements followed by a newline.
   * Leading/trailing spaces on each line are skipped (indentation in multi-line strings).
   */
  private line = this.RULE('line', () => {
    // Skip leading spaces (indentation)
    this.MANY(() => {
      this.CONSUME(Space);
    });

    // At least one inline element
    this.AT_LEAST_ONE(() => {
      this.SUBRULE(this.inlineElement);
    });

    // Skip trailing spaces before newline
    this.MANY2(() => {
      this.CONSUME2(Space);
    });

    // Line ends with newline (optional — last line may have no trailing newline)
    this.MANY3(() => {
      this.CONSUME(Newline);
    });
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // Inline Elements (within a line)
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * inlineElement: FreeText | Identifier | NumberLiteral | StringLiteral | Space | TEXT_HIGHLIGHTER[...] | W_S_I[...] | operators
   *
   * Accepts any token that can appear in natural text.
   *
   * When `displayOnly=true`, interactive widgets (currently `inlineInput`) are
   * gated out — used by MATCHER ITEM blocks to forbid inputs.
   */
  private inlineElement = this.RULE('inlineElement', (displayOnly: boolean = false) => {
    this.OR([
      // Free text (natural writing - just type it!)
      { ALT: () => this.CONSUME(FreeText) },

      // Identifiers (as free text - words like "John", "apples")
      { ALT: () => this.CONSUME(Identifier) },

      // Numbers (as free text)
      { ALT: () => this.CONSUME(NumberLiteral) },

      // Strings (as free text)
      { ALT: () => this.CONSUME(StringLiteral) },

      // Space (explicit spacing)
      { ALT: () => this.CONSUME(Space) },

      // Operators (for math expressions)
      { ALT: () => this.CONSUME(Plus) },
      { ALT: () => this.CONSUME(Minus) },
      { ALT: () => this.CONSUME(Multiply) },
      { ALT: () => this.CONSUME(Divide) },
      { ALT: () => this.CONSUME(Equals) },
      { ALT: () => this.CONSUME(LessThan) },
      { ALT: () => this.CONSUME(GreaterThan) },
      { ALT: () => this.CONSUME(Question) },

      // Punctuation
      { ALT: () => this.CONSUME(Comma) },

      // Parentheses (for grouping)
      { ALT: () => this.CONSUME(LParen) },
      { ALT: () => this.CONSUME(RParen) },

      // TEXT_HIGHLIGHTER / HIGHLIGHT_TEXT (for highlighting)
      { ALT: () => this.SUBRULE(this.textHighlighter) },

      // COLOR_TEXT (for colored text)
      { ALT: () => this.SUBRULE(this.colorText) },

      // BOLD_TEXT (for bold text)
      { ALT: () => this.SUBRULE(this.boldText) },

      // FRACTION (inline fraction widget)
      { ALT: () => this.SUBRULE(this.fraction) },

      // IMAGE (inline image element)
      { ALT: () => this.SUBRULE(this.image) },
      // VISUAL_FRACTION — always allowed; display-only when answer=[] or absence of answer parameter, interactive when answer=[...] with values.
      { ALT: () => this.SUBRULE(this.visualFraction) },

      // W_S_I (inline input widget) — excluded in display-only contexts (e.g. MATCHER ITEM)
      { GATE: () => !displayOnly, ALT: () => this.SUBRULE(this.inlineInput) },

      // INLINE_DRAG_OPTION (single draggable option inline with text) — excluded in display-only
      { GATE: () => !displayOnly, ALT: () => this.SUBRULE(this.inlineDragOption) },

      // CLOCK (inline clock widget)
      { ALT: () => this.SUBRULE(this.clock) },
    ]);
  });

  /**
   * clock: CLOCK[parameterList]
   *
   * Parameters (validated in visitor):
   * - name?, mode? ("display"|"interactive"), type ("analog"|"digital"),
   *   value (HH:MM), answer? (HH:MM), format? ("12h"|"24h"), snap?
   */
  private clock = this.RULE('clock', () => {
    this.CONSUME(CLOCK);
    this.CONSUME(LBracket);
    this.SUBRULE(this.parameterList);
    this.CONSUME(RBracket);
  });

  /**
   * textHighlighter: TEXT_HIGHLIGHTER[value="...", start=N, end=N] | HIGHLIGHT_TEXT[value="...", start=N, end=N]
   *
   * TEXT_HIGHLIGHTER = text highlighting component (alias: HIGHLIGHT_TEXT)
   * HIGHLIGHT_TEXT = alias for TEXT_HIGHLIGHTER
   */
  private textHighlighter = this.RULE('textHighlighter', () => {
    this.OR([
      { ALT: () => this.CONSUME(TEXT_HIGHLIGHTER) },
      { ALT: () => this.CONSUME(HIGHLIGHT_TEXT) },
    ]);
    this.CONSUME(LBracket);
    this.SUBRULE(this.parameterList);
    this.CONSUME(RBracket);
  });

  /**
   * colorText: COLOR_TEXT[value="...", color="red", start=N, end=N]
   */
  private colorText = this.RULE('colorText', () => {
    this.CONSUME(COLOR_TEXT);
    this.CONSUME(LBracket);
    this.SUBRULE(this.parameterList);
    this.CONSUME(RBracket);
  });

  /**
   * boldText: BOLD_TEXT[value="...", start=N, end=N]
   */
  private boldText = this.RULE('boldText', () => {
    this.CONSUME(BOLD_TEXT);
    this.CONSUME(LBracket);
    this.SUBRULE(this.parameterList);
    this.CONSUME(RBracket);
  });

  /**
   * fraction: FRACTION[name="x", numerator=5, denominator=3, answer="5/3", simplified=true]
   */
  private fraction = this.RULE('fraction', () => {
    this.CONSUME(FRACTION);
    this.CONSUME(LBracket);
    this.SUBRULE(this.parameterList);
    this.CONSUME(RBracket);
  });

  /**
   * image: IMAGE[name="fig1", src="https://...", alt="cat", width=120, height=80]
   */
  private image = this.RULE('image', () => {
    this.CONSUME(IMAGE);
    this.CONSUME(LBracket);
    this.SUBRULE(this.parameterList);
    this.CONSUME(RBracket);
  });
  /**
   * visualFraction: VISUAL_FRACTION[name="vf1", shape="bar", divisions=8, filled=[3], answer=[2]]
   */
  private visualFraction = this.RULE('visualFraction', () => {
    this.CONSUME(VISUAL_FRACTION);
    this.CONSUME(LBracket);
    this.SUBRULE(this.parameterList);
    this.CONSUME(RBracket);
  });

  /**
   * numberArray: [ number (, number)* ]
   *
   * Used for filled=[] and answer=[] parameters of VISUAL_FRACTION.
   */
  private numberArray = this.RULE('numberArray', () => {
    this.CONSUME(LBracket);

    // Skip spaces after opening bracket
    this.MANY(() => {
      this.CONSUME(Space);
    });

    // Optional list of numbers (may be empty)
    this.OPTION(() => {
      this.CONSUME(NumberLiteral, { LABEL: 'items' });

      this.MANY2(() => {
        this.MANY3(() => { this.CONSUME2(Space); });
        this.CONSUME(Comma);
        this.MANY4(() => { this.CONSUME3(Space); });
        this.CONSUME2(NumberLiteral, { LABEL: 'items' });
      });

      // Optional trailing comma
      this.OPTION2(() => {
        this.MANY5(() => { this.CONSUME4(Space); });
        this.CONSUME2(Comma);
      });
    });

    this.MANY6(() => { this.CONSUME5(Space); });
    this.CONSUME(RBracket);
  });

  /**
   * inlineInput: W_S_I[...] | STRING_INPUT[...] | W_N_I[...] | NUMERIC_INPUT[...]
   *
   * W_S_I = string input widget (alias: STRING_INPUT)
   * STRING_INPUT = alias for W_S_I
   * W_N_I = numeric input widget (alias: NUMERIC_INPUT)
   * NUMERIC_INPUT = alias for W_N_I
   * W_S_I/STRING_INPUT uses "length" parameter, W_N_I/NUMERIC_INPUT uses "digits" parameter.
   */
  private inlineInput = this.RULE('inlineInput', () => {
    this.OR([
      { ALT: () => this.CONSUME(W_S_I, { LABEL: 'stringInput' }) },
      { ALT: () => this.CONSUME(STRING_INPUT, { LABEL: 'stringInput' }) },
      { ALT: () => this.CONSUME(W_N_I, { LABEL: 'numericInput' }) },
      { ALT: () => this.CONSUME(NUMERIC_INPUT, { LABEL: 'numericInput' }) },
    ]);
    this.CONSUME(LBracket);
    this.SUBRULE(this.parameterList);
    this.CONSUME(RBracket);
  });

  /**
   * inlineDragOption: INLINE_DRAG_OPTION["value"]
   *
   * A single draggable option rendered inline within the text flow.
   *
   * @example INLINE_DRAG_OPTION["94"]
   */
  private inlineDragOption = this.RULE('inlineDragOption', () => {
    this.CONSUME(INLINE_DRAG_OPTION);
    this.CONSUME(LBracket);
    this.CONSUME(StringLiteral);
    this.CONSUME(RBracket);
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // Tools
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * tool: dragableButtons | numericKeypad
   */
  private tool = this.RULE('tool', () => {
    this.OR([
      { ALT: () => this.SUBRULE(this.dragableButtons) },
      { ALT: () => this.SUBRULE(this.numericKeypad) },
      { ALT: () => this.SUBRULE(this.rulerTool) },
      { ALT: () => this.SUBRULE(this.protractorTool) },
      { ALT: () => this.SUBRULE(this.setSquareTool) },
    ]);
  });

  /**
   * dragableButtons: DRAGABLE_BUTTONS_OPTIONS[">", "<", "="]
   */
  private dragableButtons = this.RULE('dragableButtons', () => {
    this.CONSUME(DRAGABLE_BUTTONS_OPTIONS);
    this.CONSUME(LBracket);
    this.SUBRULE(this.stringList);
    this.CONSUME(RBracket);

    // Consume newlines and spaces after tool
    this.MANY(() => {
      this.OR([{ ALT: () => this.CONSUME(Newline) }, { ALT: () => this.CONSUME(Space) }]);
    });
  });

  /**
   * radioButtons: RADIO_BUTTONS_OPTIONS[name="q1", answer="apple", options={"apple", "orange", "banana"}]
   */
  private radioButtons = this.RULE('radioButtons', () => {
    // Skip leading whitespace/newlines
    this.MANY(() => {
      this.OR([{ ALT: () => this.CONSUME(Newline) }, { ALT: () => this.CONSUME(Space) }]);
    });

    this.CONSUME(RADIO_BUTTONS_OPTIONS);
    this.CONSUME(LBracket);
    this.SUBRULE(this.parameterList);
    this.CONSUME(RBracket);

    // Consume trailing newlines
    this.MANY2(() => {
      this.OR2([{ ALT: () => this.CONSUME2(Newline) }, { ALT: () => this.CONSUME2(Space) }]);
    });
  });

  /**
   * numericKeypad: NUMERIC_KEYPAD[] or NUMERIC_KEYPAD[includeOperators=true]
   */
  private numericKeypad = this.RULE('numericKeypad', () => {
    this.CONSUME(NUMERIC_KEYPAD);
    this.CONSUME(LBracket);
    this.OPTION(() => {
      this.SUBRULE(this.parameterList);
    });
    this.CONSUME(RBracket);

    // Consume newlines and spaces after tool
    this.MANY(() => {
      this.OR([{ ALT: () => this.CONSUME(Newline) }, { ALT: () => this.CONSUME(Space) }]);
    });
  });

  /**
   * rulerTool: RULER[] or RULER[size=1]
   */
  private rulerTool = this.RULE('rulerTool', () => {
    this.CONSUME(RULER);
    this.CONSUME(LBracket);
    this.OPTION(() => {
      this.SUBRULE(this.parameterList);
    });
    this.CONSUME(RBracket);

    this.MANY(() => {
      this.OR([{ ALT: () => this.CONSUME(Newline) }, { ALT: () => this.CONSUME(Space) }]);
    });
  });

  /**
   * protractorTool: PROTRACTOR[] or PROTRACTOR[size=1]
   */
  private protractorTool = this.RULE('protractorTool', () => {
    this.CONSUME(PROTRACTOR);
    this.CONSUME(LBracket);
    this.OPTION(() => {
      this.SUBRULE(this.parameterList);
    });
    this.CONSUME(RBracket);

    this.MANY(() => {
      this.OR([{ ALT: () => this.CONSUME(Newline) }, { ALT: () => this.CONSUME(Space) }]);
    });
  });

  /**
   * setSquareTool: SET_SQUARE[] or SET_SQUARE[size=1]
   */
  private setSquareTool = this.RULE('setSquareTool', () => {
    this.CONSUME(SET_SQUARE);
    this.CONSUME(LBracket);
    this.OPTION(() => {
      this.SUBRULE(this.parameterList);
    });
    this.CONSUME(RBracket);

    this.MANY(() => {
      this.OR([{ ALT: () => this.CONSUME(Newline) }, { ALT: () => this.CONSUME(Space) }]);
    });
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // Validation
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * validation: WIDGET_ANSWER[type="simple"|"expression", ...]
   */
  private validation = this.RULE('validation', () => {
    this.CONSUME(WIDGET_ANSWER);
    this.CONSUME(LBracket);
    this.SUBRULE(this.parameterList);
    this.CONSUME(RBracket);
  });

  // ═══════════════════════════════════════════════════════════════════════════
  // Parameter Lists
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * parameterList: parameter (, parameter)*
   *
   * Note: Spaces are consumed and ignored inside parameter lists
   */
  private parameterList = this.RULE('parameterList', () => {
    // Skip leading spaces
    this.MANY(() => {
      this.CONSUME(Space);
    });

    this.SUBRULE(this.parameter);

    this.MANY2(() => {
      // Skip spaces before comma
      this.MANY3(() => {
        this.CONSUME2(Space);
      });

      this.CONSUME(Comma);

      // Skip spaces after comma
      this.MANY4(() => {
        this.CONSUME3(Space);
      });

      this.SUBRULE2(this.parameter);
    });

    // Skip trailing spaces
    this.MANY5(() => {
      this.CONSUME4(Space);
    });
  });

  /**
   * parameter: name=value
   */
  private parameter = this.RULE('parameter', () => {
    this.CONSUME(Identifier, { LABEL: 'key' });
    this.CONSUME(Equals);
    this.OR([
      // String value
      { ALT: () => this.CONSUME(StringLiteral, { LABEL: 'value' }) },

      // Number value
      { ALT: () => this.CONSUME(NumberLiteral, { LABEL: 'value' }) },

      // Boolean value (as identifier: true/false)
      { ALT: () => this.CONSUME2(Identifier, { LABEL: 'value' }) },

      // Tuple value: (start, end)
      { ALT: () => this.SUBRULE(this.tuple, { LABEL: 'value' }) },

      // Curly-brace string list: {"a", "b", "c"}
      { ALT: () => this.SUBRULE(this.curlyStringList, { LABEL: 'value' }) },

      // Square-bracket number array: [1, 2, 3]  — used by VISUAL_FRACTION filled/answer
      { ALT: () => this.SUBRULE(this.numberArray, { LABEL: 'value' }) },
    ]);
  });

  /**
   * tuple: (value, value)
   *
   * Note: Spaces are consumed and ignored inside tuples
   */
  private tuple = this.RULE('tuple', () => {
    this.CONSUME(LParen);

    // Skip spaces after opening paren
    this.MANY(() => {
      this.CONSUME(Space);
    });

    this.OR([
      { ALT: () => this.CONSUME(NumberLiteral, { LABEL: 'start' }) },
      { ALT: () => this.CONSUME(Identifier, { LABEL: 'start' }) },
    ]);

    // Skip spaces before comma
    this.MANY2(() => {
      this.CONSUME2(Space);
    });

    this.CONSUME(Comma);

    // Skip spaces after comma
    this.MANY3(() => {
      this.CONSUME3(Space);
    });

    this.OR2([
      { ALT: () => this.CONSUME2(NumberLiteral, { LABEL: 'end' }) },
      { ALT: () => this.CONSUME2(Identifier, { LABEL: 'end' }) },
    ]);

    // Skip spaces before closing paren
    this.MANY4(() => {
      this.CONSUME4(Space);
    });

    this.CONSUME(RParen);
  });

  /**
   * stringList: "str1", "str2", ...
   *
   * Note: Spaces are consumed and ignored inside string lists
   */
  private stringList = this.RULE('stringList', () => {
    // Skip leading spaces
    this.MANY(() => {
      this.CONSUME(Space);
    });

    this.CONSUME(StringLiteral);

    this.MANY2(() => {
      // Skip spaces before comma
      this.MANY3(() => {
        this.CONSUME2(Space);
      });

      this.CONSUME(Comma);

      // Skip spaces after comma
      this.MANY4(() => {
        this.CONSUME3(Space);
      });

      this.CONSUME2(StringLiteral);
    });

    // Skip trailing spaces
    this.MANY5(() => {
      this.CONSUME4(Space);
    });
  });

  /**
   * curlyStringList: { "string1", "string2", ... }
   */
  private curlyStringList = this.RULE('curlyStringList', () => {
    this.CONSUME(LBrace);
    this.SUBRULE(this.stringList);
    this.CONSUME(RBrace);
  });
}

// Export singleton instance
export const parser = new MathExerciseParser();
